/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, tap, map, catchError, from, switchMap } from 'rxjs';
import type WebMap from '@arcgis/core/WebMap';
import type MapView from '@arcgis/core/views/MapView';
import type { ArcgisAuth, ArcgisAuthResponse } from '../../models/arcgis-auth';
import type GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type Point from '@arcgis/core/geometry/Point';
import type esriConfig from '@arcgis/core/config';
import type IdentityManager from '@arcgis/core/identity/IdentityManager';
import type { Company } from '../../../../shared/interfaces/company';
import type { ArcgisUser } from '../../models/arcgis-user';
import type { ArcgisUserContentResponse } from '../../models/arcgis-user-content-response';
import { environment } from '../../../../../environments/environment';
import type LayerList from '@arcgis/core/widgets/LayerList';
import { isNullOrUndefinedOrEmpty } from '../../../../shared/utils/utils';
import { ArcGISItem, ArcGISItemType } from '../../models/arcgis-item';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';

interface GeocodeResponse {
  candidates: {
    location: {
      x: number;
      y: number;
    };
    attributes: {
      Match_addr: string;
      Addr_type: string;
    };
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class ArcgisService {
  router = inject(Router);
  http = inject(HttpClient);

  private KEY_ARCGIS_TOKEN = 'arcgis-auth-token';
  private KEY_ARCGIS_DASHBOARDS = 'arcgis-dashboards';
  private apiBaseUrl = environment.apiBaseUrl;
  private arcgisBaseUrl = 'https://arcgis.accdata.org/server/sharing/rest';

  getArcgisAuthInfoFromStorage(): ArcgisAuth | undefined {
    const storageKey = this.KEY_ARCGIS_TOKEN;
    const arcgisAuthInfo = localStorage.getItem(storageKey);
    if (arcgisAuthInfo == null) {
      return undefined;
    } else if (this._hasTokenExpired(arcgisAuthInfo)) {
      localStorage.removeItem(storageKey);
      return undefined;
    } else {
      const tokenInfo: ArcgisAuth = JSON.parse(arcgisAuthInfo);
      return tokenInfo;
    }
  }

  getAllArcgisDataFromStorage(): {
    token?: ArcgisAuth;
    dashboards?: Record<string, boolean>;
  } {
    return {
      token: this.getArcgisAuthInfoFromStorage(),
      dashboards: this.getDashboardsFromStorage(),
    };
  }

  clearArcgisTokensFromStorage(): void {
    const storageKey = this.KEY_ARCGIS_TOKEN;
    localStorage.removeItem(storageKey);
  }

  getDashboardsFromStorage(): Record<string, boolean> | undefined {
    const dashboardsInfo = localStorage.getItem(this.KEY_ARCGIS_DASHBOARDS);
    if (dashboardsInfo == null) {
      return undefined;
    } else {
      return JSON.parse(dashboardsInfo);
    }
  }

  clearDashboardsFromStorage(): void {
    localStorage.removeItem(this.KEY_ARCGIS_DASHBOARDS);
  }

  clearAllArcgisDataFromStorage(): void {
    this.clearArcgisTokensFromStorage();
    this.clearDashboardsFromStorage();
  }

  getM2MToken(): Observable<ArcgisAuthResponse> {
    return this.http
      .get<ArcgisAuthResponse>(`${this.apiBaseUrl}/api/auth/arcgis`)
      .pipe(
        tap((response) => {
          if (response.token) {
            this._setArcGISAuthInfo(response.token);
          }
          this._setDashboardsInfo(response.dashboards);
        }),
      );
  }

  async loadWebMap(
    mapContainer: HTMLDivElement,
  ): Promise<{ webMap: WebMap; view: MapView }> {
    let arcgisToken = this.getArcgisAuthInfoFromStorage()?.access_token;

    const [
      WebMapC,
      MapViewC,
      LayerListC,
      GraphicsLayerC,
      esriIdC,
      esriConfigC,
    ] = await Promise.all([
      import('@arcgis/core/WebMap').then((m) => m.default),
      import('@arcgis/core/views/MapView').then((m) => m.default),
      import('@arcgis/core/widgets/LayerList').then((m) => m.default),
      import('@arcgis/core/layers/GraphicsLayer').then((m) => m.default),
      import('@arcgis/core/identity/IdentityManager').then((m) => m.default),
      import('@arcgis/core/config.js').then((m) => m.default),
    ]);

    return new Promise((resolve, reject) => {
      if (!arcgisToken) {
        this.getM2MToken().subscribe({
          next: (authResponse: ArcgisAuthResponse) => {
            if (!authResponse.token) {
              reject(new Error('ArcGIS service is currently unavailable'));
              return;
            }
            arcgisToken = authResponse.token.access_token;
            this._initializeWebMap(
              mapContainer,
              arcgisToken,
              resolve,
              reject,
              WebMapC,
              MapViewC,
              LayerListC,
              GraphicsLayerC,
              esriIdC,
              esriConfigC,
            );
          },
          error: (err) => {
            reject(
              new Error('Failed to retrieve ArcGIS token: ' + err.message),
            );
          },
        });
      } else {
        this._initializeWebMap(
          mapContainer,
          arcgisToken,
          resolve,
          reject,
          WebMapC,
          MapViewC,
          LayerListC,
          GraphicsLayerC,
          esriIdC,
          esriConfigC,
        );
      }
    });
  }

  private _initializeWebMap(
    mapContainer: HTMLDivElement,
    arcgisToken: string,
    resolve: (value: { webMap: WebMap; view: MapView }) => void,
    reject: (reason?: unknown) => void,
    WebMapC: typeof WebMap,
    MapViewC: typeof MapView,
    LayerListC: typeof LayerList,
    GraphicsLayerC: typeof GraphicsLayer,
    esriIdC: typeof IdentityManager,
    esriConfigC: typeof esriConfig,
  ): void {
    try {
      esriConfigC.portalUrl = 'https://arcgis.accdata.org/server';
      esriIdC.registerToken({
        server: 'https://arcgis.accdata.org/server',
        token: arcgisToken,
      });

      const webMap = new WebMapC({
        portalItem: {
          apiKey: arcgisToken,
          id: environment.ArcGISWebMapId,
        },
      });

      const view = new MapViewC({
        container: mapContainer,
        map: webMap,
        zoom: 3,
      });

      // Update the LayerList widget configuration
      const layerList = new LayerListC({
        view: view,
        listItemCreatedFunction: (event: { item: any }) => {
          const item = event.item;
          if (item.layer) {
            item.panel = {
              content: 'legend',
              open: true,
            };
          }
        },
      });
      view.ui.add(layerList, {
        position: 'top-right',
      });

      // Wait for the webmap to load before adding the graphics layer
      webMap
        .when(() => {
          const graphicsLayer = new GraphicsLayerC({
            title: 'Company Markers Layer',
          });
          webMap.add(graphicsLayer);

          // Resolve after both the view and webmap are ready
          view.when(() => resolve({ webMap, view }), reject);
        })
        .catch(reject);
    } catch (error) {
      reject(error);
    }
  }

  async AddPointsToWebMap(
    webmap: WebMap,
    view: MapView,
    companies: Company[],
  ): Promise<{ webmap: WebMap; view: MapView }> {
    const arcgisToken = this.getArcgisAuthInfoFromStorage()?.access_token;

    const [GraphicC, PointC, SimpleMarkerSymbolC, PopupTemplateC] =
      await Promise.all([
        import('@arcgis/core/Graphic').then((m) => m.default),
        import('@arcgis/core/geometry/Point').then((m) => m.default),
        import('@arcgis/core/symbols/SimpleMarkerSymbol').then(
          (m) => m.default,
        ),
        import('@arcgis/core/PopupTemplate').then((m) => m.default),
      ]);

    return new Promise((resolve, reject) => {
      try {
        if (!arcgisToken) {
          return reject(new Error('ArcGIS token is undefined'));
        }

        const graphicsLayer = webmap.layers.find(
          (layer) => layer.title === 'Company Markers Layer',
        ) as unknown as GraphicsLayer;

        const newUniqueIds = new Set(
          companies.map((company) => company.unique_id),
        );
        const markersToRemove = (graphicsLayer as any).graphics.filter(
          (graphic: any) => !newUniqueIds.has(graphic.attributes.unique_id),
        );
        markersToRemove.forEach((marker: any) =>
          (graphicsLayer as any).remove(marker),
        );

        const addMarkerWithTooltip = (
          longitude: number,
          latitude: number,
          company: Company,
        ) => {
          const marker = new GraphicC({
            geometry: new PointC({
              longitude,
              latitude,
            }),
            symbol: new SimpleMarkerSymbolC({
              color: 'blue',
              size: '12px',
              outline: {
                color: 'white',
                width: 1,
              },
            }),
            attributes: { unique_id: company.unique_id },
            popupTemplate: new PopupTemplateC({
              title: company.company_name,
              content: `<p>${company.unique_id}</p><p>${company.short_address}</p>`,
            }),
          });
          graphicsLayer.add(marker);
        };

        companies.forEach((company) => {
          const existingMarker = (graphicsLayer as any).graphics.find(
            (graphic: any) =>
              graphic.attributes.unique_id === company.unique_id,
          );
          if (!existingMarker) {
            if (company.latitude && company.longitude) {
              addMarkerWithTooltip(
                company.longitude,
                company.latitude,
                company,
              );
            } else {
              const placeName = company.short_address.split(',')[0];
              this.getLatLongFromAddress(placeName).subscribe({
                next: (location) => {
                  addMarkerWithTooltip(location.x, location.y, company);
                },
                error: (error) => {
                  throw error;
                },
              });
            }
          }
        });

        view.when(() => resolve({ webmap, view }), reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLatLongFromAddress(address: string): Observable<Point> {
    const arcgisToken = this.getArcgisAuthInfoFromStorage()?.access_token;
    if (!arcgisToken) {
      return new Observable((observer) => {
        observer.error('ArcGIS token is undefined');
      });
    }

    return from(import('@arcgis/core/geometry/Point')).pipe(
      switchMap((PointModule) => {
        const PointC = PointModule.default;
        const url = `https://arcgis.accdata.org/server/sharing/servers/aaec0d98137b42c9bcdfd60e19a772e2/rest/services/World/GeocodeServer/findAddressCandidates?f=json&token=${arcgisToken}&SingleLine=${encodeURIComponent(address)}`;

        return this.http.get<GeocodeResponse>(url).pipe(
          map((response: GeocodeResponse) => {
            if (response.candidates && response.candidates.length > 0) {
              const candidate = response.candidates[0];
              if (candidate.location) {
                return new PointC({
                  longitude: candidate.location.x,
                  latitude: candidate.location.y,
                });
              } else {
                throw new Error('Location is null or undefined');
              }
            } else {
              throw new Error('No location found');
            }
          }),
        );
      }),
      catchError((error: unknown) => {
        throw error;
      }),
    );
  }

  getUserData(token: string): Observable<ArcgisUser> {
    const params = new HttpParams().set('f', 'json').set('token', token);
    return this.http.get<ArcgisUser>(`${this.arcgisBaseUrl}/community/self`, {
      params,
    });
  }

  getUserOrganizationContent(
    orgId: string,
    token: string,
  ): Observable<ArcgisUserContentResponse> {
    const searchParams = new HttpParams()
      .set('q', `orgid:${orgId}`)
      .set('f', 'json')
      .set('token', token)
      .set('num', '100');

    return this.http.get<ArcgisUserContentResponse>(
      `${this.arcgisBaseUrl}/search`,
      { params: searchParams },
    );
  }

  getUserGroupContent(
    groupId: string,
    token: string,
  ): Observable<ArcgisUserContentResponse> {
    const params = new HttpParams()
      .set('f', 'json')
      .set('token', token)
      .set('start', '1')
      .set('num', '100');

    const url = `${this.arcgisBaseUrl}/content/groups/${groupId}`;
    return this.http.get<ArcgisUserContentResponse>(url, { params }).pipe(
      tap((response) => {
        localStorage.setItem(groupId, JSON.stringify(response));
      }),
    );
  }

  getUserGroupByGroupName(
    groupName: string,
    token: string,
  ): Observable<ArcgisUserContentResponse> {
    const response = localStorage.getItem(groupName);
    if (response) {
      return of(JSON.parse(response));
    }

    const params = new HttpParams()
      .set('q', groupName)
      .set('f', 'json')
      .set('token', token)
      .set('start', '1')
      .set('num', '100');

    const url = `${this.arcgisBaseUrl}/community/groups`;
    return this.http.get<ArcgisUserContentResponse>(url, { params }).pipe(
      tap((response) => {
        localStorage.setItem(groupName, JSON.stringify(response));
      }),
    );
  }

  async setArcgisAuthContext(arcgisToken: string): Promise<boolean> {
    if (isNullOrUndefinedOrEmpty(arcgisToken)) {
      return false;
    }

    const serverUrl = 'https://arcgis.accdata.org/server';

    const esriId = await import('@arcgis/core/identity/IdentityManager').then(
      (m) => m.default,
    );

    esriId.registerToken({
      server: serverUrl,
      token: arcgisToken,
    });

    const portalSelfUrl = serverUrl + '/sharing/rest/portals/self?f=pjson';

    try {
      const response = await this.http
        .get(`${portalSelfUrl}&token=${arcgisToken}`)
        .toPromise();

      const data = response as any;
      if (data.error && data.error.code === 498) {
        return false;
      }
      return true;
    } catch (error) {
      console.error('❌ Error validating server token:', error);
      return false;
    }
  }

  saveArcGISItem(item: ArcGISItem): Observable<ArcGISItem> {
    return this.http.post<ArcGISItem>(
      `${this.apiBaseUrl}/api/arcgis/items`,
      item,
    );
  }

  getArcGISItems(
    itemType?: ArcGISItemType,
    pageNo = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<ArcGISItem>> {
    let params = new HttpParams();
    params = params.set('page', pageNo.toString());
    params = params.set('page_size', pageSize.toString());
    if (itemType !== undefined) {
      params = params.set('item_type', itemType.toString());
    }
    return this.http.get<PaginatedResponse<ArcGISItem>>(
      `${this.apiBaseUrl}/api/arcgis/items`,
      { params },
    );
  }

  deleteArcGISItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/api/arcgis/items/${id}`);
  }

  private _setArcGISAuthInfo(arcgisAuthInfo: ArcgisAuth): void {
    arcgisAuthInfo.expires_in =
      arcgisAuthInfo.expires_in * 1000 + new Date().getTime();
    const storageKey = this.KEY_ARCGIS_TOKEN;
    localStorage.setItem(storageKey, JSON.stringify(arcgisAuthInfo));
  }

  private _setDashboardsInfo(dashboards: Record<string, boolean>): void {
    localStorage.setItem(
      this.KEY_ARCGIS_DASHBOARDS,
      JSON.stringify(dashboards),
    );
  }

  private _hasTokenExpired(arcgisAuthInfo: string): boolean {
    const expiry: number = JSON.parse(arcgisAuthInfo).expires_in;
    return new Date().getTime() > expiry;
  }
}
